// The navigation-rail layout (on by default): a permanent labelled rail on the left
// and ONE content panel beside it, showing a single section at a time.
//
// Sections are listed here so a future section joins the same pattern by adding an entry —
// the rail renders its icon, the panel its heading.

/** Width of the rail, in CSS px: an icon with its name under it. */
export const RAIL_W = 72;

/**
 * The rail's main items, top to bottom under the wordmark. The first three open a panel beside
 * the rail and their order is load-bearing (App reads `RAIL_SECTIONS[i].heading`). `inRail: false`
 * is a panel with no rail item. The signals drawer opens from the signal bar, not the rail.
 */
export const RAIL_SECTIONS = Object.freeze([
  { id: "forecasts", label: "Forecasts", heading: "Forecasts" },
  { id: "chat", label: "Chat", heading: "Chat" },
  { id: "question", label: "Question", heading: "Resolution Criteria", inRail: false },
]);

/** Tools listed under the sections (they open their own surfaces, not the rail panel). */
export const RAIL_TOOLS = Object.freeze([
  { id: "track-record", label: "Track record", stacked: true },
]);

/** The items pinned to the rail's foot. */
export const RAIL_FOOTER = Object.freeze([
  { id: "settings", label: "Settings" },
  { id: "account", label: "Account" },
]);

/** The section open when the experiment is first turned on. */
export const RAIL_DEFAULT_SECTION = "chat";

/** localStorage key holding the panel width, shared by every section. */
export const RAIL_WIDTH_KEY = "sooth-rail-width";

/**
 * Clamp a panel width into [min, a third of the viewport]. The floor wins on a window too
 * narrow for a third to reach it. Non-finite input reads as the minimum.
 *
 * @param {number} w
 * @param {number} min
 * @param {number} [viewportW]
 * @returns {number}
 */
export function clampRailPanelWidth(w, min, viewportW = Infinity) {
  const max = Math.max(min, viewportW / 3);
  if (!Number.isFinite(w)) return min;
  return Math.round(Math.min(max, Math.max(min, w)));
}

/**
 * The next panel state after a section is opened or closed. `active` is the visible section
 * (null when collapsed); `last` is the one the expand control re-opens; `switched` marks a
 * direct swap between two sections, which the panels render without their slide so the two
 * are never on screen together.
 *
 * @param {{active: string|null, last: string}} prev
 * @param {string|null} active
 * @returns {{active: string|null, last: string, switched: boolean}}
 */
export function nextRailState(prev, active) {
  return {
    active,
    last: active ?? prev.last,
    switched: !!(prev.active && active && prev.active !== active),
  };
}

/**
 * Apply a `setXOpen(value | (open) => value)` call for one section to the rail state: open
 * makes it the active section, close collapses the panel only if it was the one showing.
 *
 * @param {{active: string|null, last: string}} prev
 * @param {string} sectionId
 * @param {boolean | ((open: boolean) => boolean)} value
 * @returns {{active: string|null, last: string, switched: boolean}}
 */
export function setSectionOpen(prev, sectionId, value) {
  const isOpen = prev.active === sectionId;
  const open = typeof value === "function" ? value(isOpen) : value;
  if (open) return nextRailState(prev, sectionId);
  return isOpen ? nextRailState(prev, null) : { ...prev, switched: false };
}

/**
 * Which rail icon the tour flashes: Forecasts while its "explore" step waits for a click,
 * then Chat once the return hint is due, until the user leaves the Forecast Manager. Chat
 * also flashes whenever a tour step was narrated while the Chat Console was not showing, or
 * once the user has stayed in another panel too long (`awayTooLong`).
 *
 * @param {{enabled: boolean, tourSpotlight: string|null, logOpen: boolean,
 *   inManager: boolean, returnHintDue: boolean, unseenTourStep?: boolean,
 *   awayTooLong?: boolean}} s
 * @returns {"forecasts"|"chat"|null}
 */
export function railTourFlashSection({
  enabled,
  tourSpotlight,
  logOpen,
  inManager,
  returnHintDue,
  unseenTourStep = false,
  awayTooLong = false,
}) {
  if (enabled && tourSpotlight === "tab" && !logOpen) return "forecasts";
  if (inManager && returnHintDue) return "chat";
  if (enabled && (unseenTourStep || awayTooLong)) return "chat";
  return null;
}
